import React, { useEffect, useMemo, useState } from 'react';

import { useGetSample } from '@vlabs/api/artemy/client';
import type { EventRecord } from '@vlabs/api/artemy/model';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { Tag } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './LiveEventCard.module.sass';
import { BaseEventPreview } from '../../workspace/event-roll/event-preview/BaseEventPreview';

interface SampleSource {
  type: 'faces' | 'bodies';
  id: string;
}

const findSample = (detections: EventRecord['face_detections']): string | undefined => (
  detections?.find((detection) => detection.sample_id)?.sample_id ?? undefined
);

const getSampleSource = (event: EventRecord): SampleSource | undefined => {
  const faceSampleId = findSample(event.face_detections);
  if (faceSampleId) return { type: 'faces', id: faceSampleId };

  const bodySampleId = findSample(event.body_detections);
  if (bodySampleId) return { type: 'bodies', id: bodySampleId };

  return undefined;
};

const useSampleUrl = (event: EventRecord): string | undefined => {
  const source = useMemo(() => getSampleSource(event), [event]);
  const [url, setUrl] = useState<string | undefined>(undefined);

  const sample = useGetSample(
    source?.type ?? 'faces',
    source?.id ?? '',
    { query: { enabled: !!source } },
  );

  useEffect(() => {
    if (!source || !sample.data) return undefined;

    const objectUrl = URL.createObjectURL(sample.data);
    setUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [source, sample.data]);

  useEffect(() => {
    if (!source) setUrl(undefined);
  }, [source]);

  return url;
};

interface LiveEventCardProps {
  event: EventRecord;
}

// Карточка события ретрансляции — аналог карточки Панели оператора,
// но без адреса и источника. Клик без действия (модалка быстрого просмотра отложена).
function LiveEventCard({ event }: LiveEventCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const sampleUrl = useSampleUrl(event);

  let typeLabel = t('lunapoint:карточка события.обнаружение');
  if (event.top_match) {
    typeLabel = t('lunapoint:карточка события.совпадение');
  }

  return (
    <BaseEventPreview
      data-testid={`live-event-${event.event_id}`}
      onClick={() => {}}
    >
      <BaseEventPreview.Main>
        <BaseEventPreview.Media>
          {!!sampleUrl && (
            <img
              alt={typeLabel}
              className={st.Sample}
              src={sampleUrl}
            />
          )}
        </BaseEventPreview.Media>

        <BaseEventPreview.Info>
          <div className={st.InfoContent}>
            <Tag
              kind="neutral"
              value={typeLabel}
            />
            {!!event.create_time && (
              <div className={st.DateTime}>
                {formatDateTime(event.create_time)}
              </div>
            )}
          </div>
        </BaseEventPreview.Info>
      </BaseEventPreview.Main>
    </BaseEventPreview>
  );
}

export { LiveEventCard };
